import * as React from 'react'
import clsx from 'clsx'
import { useIsFocusVisible } from '../../hooks/useIsFocusVisible'
import { useForkRef } from '../../hooks/useForkRef'
import { Typography, TypographyProps } from '../Typography'
import { SxProps } from '../sx'
import styles from './index.module.scss'
import { capitalize } from '../../utils'

export interface LinkClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `underline="none"`. */
  underlineNone: string
  /** Styles applied to the root element if `underline="hover"`. */
  underlineHover: string
  /** Styles applied to the root element if `underline="always"`. */
  underlineAlways: string
  /** Styles applied to the root element if `component="button"`. */
  button: string
  /** State class applied to the root element if the link is keyboard focused. */
  focusVisible: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** State class applied to the root element if `isActive={true}`. */
  active: string
}

export interface LinkProps extends React.HTMLAttributes<HTMLAnchorElement> {
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default a
   */
  Component?: React.ElementType
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<LinkClasses>
  /**
   * The color of the link.
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * `classes` prop applied to the [`Typography`](/material-ui/api/typography/) element.
   */
  TypographyClasses?: TypographyProps['classes']
  /**
   * Controls when the link should have an underline.
   * @default 'none'
   */
  underline?: 'none' | 'hover' | 'always'
  /**
   * Applies the theme typography styles.
   * @default 'inherit'
   */
  variant?: TypographyProps['variant']
  /**
   * @ignore
   */
  href?: string
  /**
   * @ignore
   */
  to?: string
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, the component is active.
   * @default false
   */
  isActive?: boolean
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  function Link(props, ref) {
    const {
      className,
      color = 'primary',
      Component = 'a',
      disabled = false,
      isActive = false,
      onBlur,
      onFocus,
      TypographyClasses,
      underline = 'none',
      variant = 'span',
      sx,
      classes,
      children,
      ...other
    } = props

    const {
      isFocusVisibleRef,
      onBlur: handleBlurVisible,
      onFocus: handleFocusVisible,
      ref: focusVisibleRef,
    } = useIsFocusVisible()
    const [focusVisible, setFocusVisible] = React.useState(false)
    const handlerRef = useForkRef(ref, focusVisibleRef)
    const handleBlur = (event: React.FocusEvent) => {
      handleBlurVisible(event)
      if (isFocusVisibleRef.current === false) {
        setFocusVisible(false)
      }
      if (onBlur) {
        onBlur(event as React.FocusEvent<HTMLAnchorElement, Element>)
      }
    }
    const handleFocus = (event: React.FocusEvent) => {
      handleFocusVisible(event)
      if (isFocusVisibleRef.current === true) {
        setFocusVisible(true)
      }
      if (onFocus) {
        onFocus(event as React.FocusEvent<HTMLAnchorElement, Element>)
      }
    }

    return (
      <Typography
        className={clsx(
          styles.root,
          classes?.root,
          [
            styles[`underline-${underline}`],
            // @ts-ignore
            classes?.[`underline${capitalize(underline)}`],
          ],
          styles[`${color}`],
          disabled && [styles.disabled, classes?.disabled],
          isActive && [styles.active, classes?.active],
          Component === 'button' && [styles.button, classes?.button],
          Component === 'button' &&
            focusVisible && [styles.focusVisible, classes?.focusVisible],
          className
        )}
        classes={TypographyClasses}
        component={Component}
        onBlur={handleBlur}
        onFocus={handleFocus}
        ref={handlerRef}
        variant={variant}
        sx={sx}
        {...other}
      >
        {children}
      </Typography>
    )
  }
)
